// Indicator registry for BCH Radar.
//
// Every indicator has a documented reason for inclusion, a data source,
// a direction (whether higher values are supportive or negative for liquidity),
// and a group. The scoring engine consumes this registry — adding a new
// indicator here (plus a collector) extends the system without a rewrite.

export type IndicatorGroup =
  | 'fed'
  | 'japan'
  | 'treasury'
  | 'global'
  | 'energy'
  | 'bch';

export type Frequency = 'realtime' | 'daily' | 'weekly';

export type ScoreDomain = 'liquidity' | 'bch';

export interface IndicatorDef {
  id: string;
  label: string;
  short: string;
  group: IndicatorGroup;
  domain: ScoreDomain;
  unit: string;
  frequency: Frequency;
  /**
   * +1 means higher values are supportive of liquidity / BCH setup.
   * -1 means higher values are negative (e.g. DXY, yields).
   */
  direction: 1 | -1;
  /** Relative weight within its domain. */
  weight: number;
  source: string;
  sourceUrl?: string;
  /** Why this indicator is included. */
  why: string;
  /** How it affects the score. */
  mechanism: string;
  /**
   * Reference band used to normalize the raw value into a 0-100 sub-score.
   * `low` maps to the least supportive end, `high` to the most supportive.
   * Direction is applied on top of this.
   */
  band: { low: number; high: number };
}

export const INDICATORS: IndicatorDef[] = [
  // ---------------- FED / U.S. ----------------
  {
    id: 'fed_balance_sheet',
    label: 'Federal Reserve Balance Sheet',
    short: 'Fed Balance Sheet',
    group: 'fed',
    domain: 'liquidity',
    unit: 'USD trn',
    frequency: 'weekly',
    direction: 1,
    weight: 3,
    source: 'FRED (WALCL)',
    sourceUrl: 'https://fred.stlouisfed.org/series/WALCL',
    why: 'The size of the Fed balance sheet is the single largest driver of USD base liquidity.',
    mechanism: 'Expansion adds reserves to the banking system, easing funding and supporting risk assets.',
    band: { low: 6.5, high: 9.0 },
  },
  {
    id: 'fima_repo',
    label: 'FIMA Repo Usage',
    short: 'FIMA Repo',
    group: 'fed',
    domain: 'liquidity',
    unit: 'USD bn',
    frequency: 'weekly',
    direction: 1,
    weight: 2,
    source: 'FRED (WLRRAFOIAL)',
    sourceUrl: 'https://fred.stlouisfed.org/',
    why: 'FIMA lets foreign central banks raise USD against Treasuries, a pressure-release valve for global dollar stress.',
    mechanism: 'Rising usage signals the Fed is providing offshore dollar liquidity, relieving funding stress.',
    band: { low: 0, high: 60 },
  },
  {
    id: 'fed_fx_assets',
    label: 'Fed Foreign Currency Assets',
    short: 'Fed FX Assets',
    group: 'fed',
    domain: 'liquidity',
    unit: 'USD bn',
    frequency: 'weekly',
    direction: 1,
    weight: 1,
    source: 'FRED',
    why: 'Reflects the scale of central-bank swap-line activity supplying dollars abroad.',
    mechanism: 'Growth indicates active swap lines relieving global dollar shortages.',
    band: { low: 0, high: 500 },
  },
  {
    id: 'tga',
    label: 'Treasury General Account',
    short: 'TGA',
    group: 'fed',
    domain: 'liquidity',
    unit: 'USD bn',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'US Treasury / FRED (WTREGEN)',
    why: 'Cash parked at the TGA is drained from bank reserves; drawdowns release liquidity.',
    mechanism: 'A falling TGA injects reserves into the system (supportive); a rising TGA drains them.',
    band: { low: 300, high: 850 },
  },
  {
    id: 'reverse_repo',
    label: 'Reverse Repo Balances',
    short: 'RRP',
    group: 'fed',
    domain: 'liquidity',
    unit: 'USD bn',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'FRED (RRPONTSYD)',
    why: 'The RRP facility parks idle cash at the Fed, removing it from markets.',
    mechanism: 'A declining RRP releases cash back into markets (supportive); a rising RRP sterilizes it.',
    band: { low: 0, high: 2200 },
  },
  {
    id: 'sofr',
    label: 'SOFR / Funding Conditions',
    short: 'SOFR',
    group: 'fed',
    domain: 'liquidity',
    unit: '%',
    frequency: 'daily',
    direction: -1,
    weight: 1,
    source: 'NY Fed',
    why: 'SOFR spikes reveal funding stress in overnight repo markets.',
    mechanism: 'Elevated SOFR relative to policy signals tightening funding (negative for liquidity).',
    band: { low: 3.5, high: 5.5 },
  },

  // ---------------- JAPAN ----------------
  {
    id: 'usdjpy',
    label: 'USD/JPY Exchange Rate',
    short: 'USD/JPY',
    group: 'japan',
    domain: 'liquidity',
    unit: '¥',
    frequency: 'realtime',
    direction: -1,
    weight: 3,
    source: 'Frankfurter (ECB)',
    why: 'A weakening yen is a barometer of global carry-trade stress and BOJ policy pressure.',
    mechanism: 'Rapid yen weakness raises the risk of disruptive intervention and carry unwinds (negative).',
    band: { low: 130, high: 162 },
  },
  {
    id: 'jgb_10y',
    label: 'Japanese 10Y Government Bond Yield',
    short: 'JGB 10Y',
    group: 'japan',
    domain: 'liquidity',
    unit: '%',
    frequency: 'daily',
    direction: -1,
    weight: 1,
    source: 'Japan MoF (JGB daily)',
    sourceUrl: 'https://www.mof.go.jp/english/policy/jgbs/reference/interest_rate/jgbcme.htm',
    why: 'Rising JGB yields signal BOJ policy normalization, which drains global yen liquidity.',
    mechanism: 'Higher JGB yields pull capital home to Japan, tightening global liquidity (negative).',
    band: { low: 0.4, high: 3.0 },
  },
  {
    id: 'japan_ust_holdings',
    label: "Japan's U.S. Treasury Holdings",
    short: 'Japan UST',
    group: 'japan',
    domain: 'liquidity',
    unit: 'USD trn',
    frequency: 'weekly',
    direction: 1,
    weight: 1,
    source: 'US Treasury TIC',
    why: 'Japan is the largest foreign holder of Treasuries; selling to defend the yen tightens conditions.',
    mechanism: 'Stable/rising holdings are neutral-to-supportive; forced selling drains dollar liquidity.',
    band: { low: 1.0, high: 1.3 },
  },

  // ---------------- TREASURY / GLOBAL ----------------
  {
    id: 'ust_2y',
    label: 'U.S. 2-Year Treasury Yield',
    short: 'US 2Y',
    group: 'treasury',
    domain: 'liquidity',
    unit: '%',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'FRED (DGS2)',
    why: 'The 2Y encodes the market\u2019s expected policy path over the near term.',
    mechanism: 'Falling 2Y yields imply expected easing (supportive); rising yields imply tightening.',
    band: { low: 3.0, high: 5.2 },
  },
  {
    id: 'ust_10y',
    label: 'U.S. 10-Year Treasury Yield',
    short: 'US 10Y',
    group: 'treasury',
    domain: 'liquidity',
    unit: '%',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'FRED (DGS10)',
    why: 'The 10Y is the global cost-of-capital anchor for risk assets.',
    mechanism: 'Elevated long yields raise discount rates and tighten financial conditions (negative).',
    band: { low: 3.2, high: 5.0 },
  },
  {
    id: 'dxy',
    label: 'U.S. Dollar Index (DXY)',
    short: 'DXY',
    group: 'treasury',
    domain: 'liquidity',
    unit: 'index',
    frequency: 'realtime',
    direction: -1,
    weight: 3,
    source: 'Frankfurter (ECB)',
    why: 'A strong dollar is the tightest global financial condition; it drains ex-US liquidity.',
    mechanism: 'A rising DXY tightens global liquidity and pressures risk assets (negative).',
    band: { low: 96, high: 108 },
  },
  {
    id: 'global_liquidity_proxy',
    label: 'Global Liquidity Proxy',
    short: 'Global Liquidity',
    group: 'global',
    domain: 'liquidity',
    unit: 'USD trn',
    frequency: 'weekly',
    direction: 1,
    // Lower weight than the Fed balance sheet it is derived from, to avoid
    // double-counting WALCL (the two move in lockstep). It adds a scaled
    // "global" framing, not independent signal.
    weight: 1,
    source: 'Derived (Fed WALCL, partial)',
    why: 'A scaled proxy for global central-bank liquidity, extrapolated from the Fed balance sheet (the largest single component). It is NOT a true multi-bank composite — no live ECB/BOJ/PBoC feed is wired in yet — so treat it as an indicative global framing of the Fed trend, not an independent measurement.',
    mechanism: 'Expansion of central-bank liquidity is broadly supportive of risk assets. Because this is extrapolated from WALCL, it moves with the Fed balance sheet.',
    band: { low: 28, high: 34 },
  },
  {
    id: 'stablecoin_supply',
    label: 'Stablecoin Supply',
    short: 'Stablecoins',
    group: 'global',
    domain: 'liquidity',
    unit: 'USD bn',
    frequency: 'daily',
    direction: 1,
    weight: 2,
    source: 'CoinGecko',
    sourceUrl: 'https://www.coingecko.com/',
    why: 'Stablecoin supply is crypto-native dry powder and a real-time liquidity proxy.',
    mechanism: 'A growing supply reflects capital entering the crypto system (supportive).',
    band: { low: 120, high: 260 },
  },

  // ---------------- ENERGY / GEOPOLITICS ----------------
  {
    id: 'wti_crude',
    label: 'WTI Crude Oil',
    short: 'WTI Crude',
    group: 'energy',
    domain: 'liquidity',
    unit: 'USD',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'FRED (DCOILWTICO)',
    sourceUrl: 'https://fred.stlouisfed.org/series/DCOILWTICO',
    why: 'Energy is a core inflation input; rising crude pressures central banks to keep policy tighter.',
    mechanism: 'Higher oil raises inflation expectations and the odds of higher-for-longer rates (negative for liquidity).',
    band: { low: 55, high: 100 },
  },
  {
    id: 'brent_crude',
    label: 'Brent Crude Oil',
    short: 'Brent Crude',
    group: 'energy',
    domain: 'liquidity',
    unit: 'USD',
    frequency: 'daily',
    direction: -1,
    weight: 1,
    source: 'FRED (DCOILBRENTEU)',
    sourceUrl: 'https://fred.stlouisfed.org/series/DCOILBRENTEU',
    why: 'Brent is the global seaborne oil benchmark — the price the rest of the world actually pays.',
    mechanism: 'Rising Brent tightens global financial conditions via higher energy costs (negative).',
    band: { low: 58, high: 105 },
  },
  {
    id: 'brent_wti_spread',
    label: 'Brent–WTI Spread (Shipping / Geopolitical Stress)',
    short: 'Brent-WTI Spread',
    group: 'energy',
    domain: 'liquidity',
    unit: 'USD',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'Derived (FRED)',
    why: 'Brent is waterborne while WTI is landlocked US; a widening spread signals seaborne supply/shipping disruption — the market\u2019s real-time read on risks like a Strait of Hormuz blockage.',
    mechanism: 'A widening Brent-WTI spread flags energy-supply and shipping stress, a geopolitical risk-off signal (negative for liquidity).',
    band: { low: 2, high: 12 },
  },
  {
    id: 'hormuz_risk',
    label: 'Oil-Market Stress (Hormuz Proxy)',
    short: 'Oil-Market Stress',
    group: 'energy',
    domain: 'liquidity',
    unit: 'index',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'Derived (Brent + Brent–WTI)',
    why: 'Roughly a fifth of global oil transits the Strait of Hormuz, so a blockage is the single largest energy-supply tail risk. There is NO free real-time feed for actual shipping, conflict, or strait status — so this index measures only how much stress the OIL MARKET is pricing in. It is a lagging, market-implied proxy, not a live geopolitical event tracker. A low reading means oil markets are calm; it does NOT mean the strait is safe.',
    mechanism: 'A composite 0–100 index that rises when Brent trades well above its calm baseline and the Brent–WTI (seaborne) spread widens. Because it is built purely from oil prices, it can under-react to an unfolding event until the market itself reacts. Treat it as one input, not a definitive read on the strait. A higher reading is risk-off (negative for liquidity).',
    band: { low: 0, high: 100 },
  },

  // ---------------- BCH SETUP ----------------
  {
    id: 'bch_usd',
    label: 'BCH / USD',
    short: 'BCH/USD',
    group: 'bch',
    domain: 'bch',
    unit: 'USD',
    frequency: 'realtime',
    direction: 1,
    weight: 2,
    source: 'CoinGecko',
    why: 'The primary price of BCH; the base for setup and momentum measures.',
    mechanism: 'A rising trend indicates strengthening demand for BCH.',
    band: { low: 150, high: 600 },
  },
  {
    id: 'bch_btc',
    label: 'BCH / BTC',
    short: 'BCH/BTC',
    group: 'bch',
    domain: 'bch',
    // Stored as a BTC ratio (~0.003); the formatter renders it ×1e8 as "sats"
    // (the crypto-standard display). Band values below are in BTC.
    unit: 'sats',
    frequency: 'realtime',
    direction: 1,
    weight: 3,
    source: 'CoinGecko',
    why: 'BCH/BTC is the purest measure of whether BCH is outperforming Bitcoin.',
    mechanism: 'A rising BCH/BTC ratio shows capital rotating into BCH (supportive for setup).',
    band: { low: 0.0025, high: 0.006 },
  },
  {
    id: 'bch_volume',
    label: 'BCH 24h Volume',
    short: 'BCH Volume',
    group: 'bch',
    domain: 'bch',
    unit: 'USD bn',
    frequency: 'realtime',
    direction: 1,
    weight: 1,
    source: 'CoinGecko',
    why: 'Volume confirms whether price moves have participation behind them.',
    mechanism: 'Rising volume alongside price improves the quality of the setup.',
    band: { low: 0.15, high: 1.5 },
  },
  {
    id: 'bch_momentum',
    label: 'BCH Momentum (30d)',
    short: 'BCH Momentum',
    group: 'bch',
    domain: 'bch',
    unit: '%',
    frequency: 'daily',
    direction: 1,
    weight: 2,
    source: 'Derived (CoinGecko)',
    why: 'Trend momentum captures whether BCH is in an up- or down-phase.',
    mechanism: 'Positive 30-day momentum supports a developing/improving setup.',
    band: { low: -25, high: 25 },
  },
  {
    id: 'bch_rel_strength',
    label: 'BCH Relative Strength vs BTC',
    short: 'BCH Rel. Strength',
    group: 'bch',
    domain: 'bch',
    unit: '%',
    frequency: 'daily',
    direction: 1,
    weight: 2,
    source: 'Derived (CoinGecko)',
    why: 'The gap between BCH and BTC 30-day performance — is BCH outrunning or lagging Bitcoin?',
    mechanism: 'Computed as BCH 30d % minus BTC 30d %. A positive gap means BCH is outperforming BTC (supportive for the setup).',
    band: { low: -15, high: 15 },
  },
  {
    id: 'btc_trend',
    label: 'BTC Trend (30d)',
    short: 'BTC Trend',
    group: 'bch',
    domain: 'bch',
    unit: '%',
    frequency: 'daily',
    direction: 1,
    weight: 2,
    source: 'Derived (CoinGecko)',
    why: 'BCH rarely sustains strength while Bitcoin is in a downtrend.',
    mechanism: 'A constructive BTC trend is a precondition for a durable BCH setup.',
    band: { low: -20, high: 20 },
  },
  {
    id: 'btc_dominance',
    label: 'BTC Dominance',
    short: 'BTC Dominance',
    group: 'bch',
    domain: 'bch',
    unit: '%',
    frequency: 'daily',
    direction: -1,
    weight: 2,
    source: 'CoinGecko',
    why: 'Falling dominance often coincides with altcoin (incl. BCH) rotation.',
    mechanism: 'Declining BTC dominance is supportive of BCH relative performance.',
    band: { low: 48, high: 62 },
  },
  {
    id: 'total_mcap',
    label: 'Total Crypto Market Cap',
    short: 'Total Mcap',
    group: 'bch',
    domain: 'bch',
    unit: 'USD trn',
    frequency: 'realtime',
    direction: 1,
    weight: 1,
    source: 'CoinGecko',
    why: 'The overall size of the crypto market frames risk appetite.',
    mechanism: 'A rising total market cap reflects capital inflows supportive of all crypto.',
    band: { low: 1.5, high: 3.5 },
  },
];

export const INDICATORS_BY_ID: Record<string, IndicatorDef> =
  Object.fromEntries(INDICATORS.map((i) => [i.id, i]));

/**
 * Derive the Strait of Hormuz risk index (0–100) from oil-market stress.
 *
 * There is no free, real-time feed for chokepoint-disruption risk, so we build a
 * market-implied substitute from the two signals that spike together when
 * Hormuz supply is threatened:
 *   1. Brent trading above its "calm" baseline (~$70), capped near a crisis high.
 *   2. A widening Brent–WTI (seaborne vs. landlocked) spread.
 *
 * The two components are blended 60/40 into a single 0–100 index where higher
 * means greater implied disruption risk.
 */
export function deriveHormuzRisk(
  brent: number | null | undefined,
  brentWtiSpread: number | null | undefined,
): number | null {
  if (typeof brent !== 'number' || Number.isNaN(brent)) return null;

  const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

  // Brent stress: $70 calm → $130 crisis.
  const brentStress = clamp01((brent - 70) / (130 - 70));

  // Spread stress: $2 normal → $14 acute disruption.
  const spread =
    typeof brentWtiSpread === 'number' && !Number.isNaN(brentWtiSpread)
      ? brentWtiSpread
      : 4;
  const spreadStress = clamp01((spread - 2) / (14 - 2));

  const index = (0.6 * brentStress + 0.4 * spreadStress) * 100;
  return Math.round(index * 10) / 10;
}

export const GROUP_LABELS: Record<IndicatorGroup, string> = {
  fed: 'Fed / U.S.',
  japan: 'Japan',
  treasury: 'Treasury / Rates',
  global: 'Global',
  energy: 'Energy / Geopolitics',
  bch: 'BCH & Crypto',
};

export const GROUP_FLAGS: Record<IndicatorGroup, string> = {
  fed: '🇺🇸',
  japan: '🇯🇵',
  treasury: '🏛️',
  global: '🌍',
  energy: '🛢️',
  bch: '🟢',
};
